import { Accordion } from '@3pc/accordion';

export class ListExpander {
  constructor(options) {
    this.listContainer = options.listContainer;
    this.list = options.list;
    this.handler = options.handler;
    this.items = this.list?.children;
    this.visibleRows = options.visibleRows || 1;
    this.duration = options.duration || 700;
  }

  init() {
    if (this.items.length <= 0) return;

    // Make list focusable.
    this.list.setAttribute('tabindex', -1);

    this.accordionInstance = new Accordion({
      target: this.listContainer,
      targetInner: this.list,
      handler: this.handler,
      noDisplayNone: true,
      initOpened: true,
      duration: this.duration,
    });
    this.accordionInstance.init();
    this.setupResizeObserver();
    this.accordionInstance.close();
    this.accordionInstance.isAnimating = false;

    this.eventBinding();
  }

  destroy() {
    this.accordionInstance.destroy();
    this.deactivateResizeObserver();
    this.listContainer.style.minHeight = '';
    this.list.style.minHeight = '';
    this.list.removeAttribute('tabindex');
    this.eventUnbinding();
  }

  eventBinding() {
    this.handler.addEventListener('click', this.focusHandler.bind(this));
  }

  eventUnbinding() {
    this.handler.removeEventListener('click', this.focusHandler);
  }

  updateMinHeight() {
    let currentY = 0;
    let rowCount = 0;
    const targetHeights = [];
    let lastVisibleRowLastItemIndex = 0;

    // Clear hiding classes for a clean start.
    for (let i = 0; i < this.items.length; i++) {
      this.items[i].classList.remove('is-hidden-on-close');
    }

    // Loop over all list items (1st level) and stop loop if no item of a visible row on closed is concerned, see `break`.
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      const itemY = item.offsetTop;
      const itemHeight = item.offsetHeight;
      let currentRowCount = rowCount;
      let rowGap = 0;

      // Check whether we a iterating in a new row.
      if (itemY > currentY) {
        // Determine a possible row gap.
        if (rowCount > 0)
          rowGap = itemY - (currentY + targetHeights[rowCount - 1]);
        // Update row count and the row distinguishing criterion currentY.
        currentY = itemY;
        rowCount++;
        currentRowCount = rowCount;
      }

      // Break loop if item is in a row that should not be visible on close.
      if (rowCount > this.visibleRows) {
        // Register last index in last visible row
        lastVisibleRowLastItemIndex = i - 1;
        break;
      }

      // If no target height is set for row set it.
      if (!targetHeights[rowCount - 1])
        targetHeights.splice(rowCount - 1, 1, 0);

      // Check whether there is a vertical gap to be considered between rows.
      if (
        rowCount === currentRowCount &&
        targetHeights[rowCount - 1] < itemHeight + rowGap
      ) {
        targetHeights[rowCount - 1] = itemHeight + rowGap;
      }
    }

    // Sum up all max row heights.
    const finalTargetHeight = targetHeights.reduce(
      (accumulator, currentValue) => {
        return accumulator + currentValue;
      }
    );

    // Set min-height to reveal targeted row.
    this.listContainer.style.minHeight = `${finalTargetHeight}px`;
    this.list.style.minHeight = `${finalTargetHeight}px`;

    // Add hiding class where needed
    for (let i = 0; i < this.items.length; i++) {
      if (i > lastVisibleRowLastItemIndex)
        this.items[i].classList.add('is-hidden-on-close');
    }
  }

  setupResizeObserver() {
    // This should hopefully account for loading images and successive renderings.
    const config = { attributes: false, childList: true, subtree: true };

    this.resizeObserver = new ResizeObserver(entries => {
      // Avoid error: "ResizeObserver loop completed with undelivered notifications."
      // caused by too many notification in a single animation frame.
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }

        if (!this.accordionInstance.show)
          setTimeout(() => {
            this.updateMinHeight();
          }, 0);
      });
    });

    this.resizeObserver.observe(this.list, config);
  }

  deactivateResizeObserver() {
    this.resizeObserver.disconnect();
  }

  focusHandler() {
    this.list.focus();
  }
}
